// ---------------------------------------------------------------------------
// Popup Trigger Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";
import { renderConversionActionControl } from "../conversion-action.js";

interface PopupTriggerProps {
  id?: string;
  trigger: "exit_intent" | "scroll_depth" | "time_delay";
  scrollPercentage?: number;
  delaySeconds?: number;
  heading: string;
  content?: string;
  ctaText: string;
  actionUrl?: string;
  actionRef?: string;
  actionPayload?: Record<string, unknown> | string;
  openInNewTab?: boolean;
  showOnce?: boolean;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as PopupTriggerProps;

  const trigger = p.trigger ?? "time_delay";
  const scrollPct = p.scrollPercentage ?? 50;
  const delaySec = p.delaySeconds ?? 10;
  const heading = p.heading ?? "";
  const ctaText = p.ctaText ?? "";
  const showOnce = p.showOnce !== false;

  const contentHtml = p.content
    ? `\n      <p class="ease-popup-body">${text(p.content)}</p>`
    : "";
  const ctaHtml = renderConversionActionControl({
    props,
    label: ctaText,
    className: "ease-btn ease-btn-primary ease-popup-cta",
    defaultUrl: "#form",
    componentType: "popup-trigger",
    componentId: p.id,
    closePopupOnClick: true,
  });

  const script = `<script>(function(){var popup=document.querySelector('[data-ease-popup]');if(!popup)return;var trigger=popup.dataset.trigger;var shown=false;var key='ease_popup_'+popup.dataset.trigger;var showOnce=${String(showOnce)};function show(){if(shown)return;if(showOnce&&sessionStorage.getItem(key))return;shown=true;popup.removeAttribute('hidden');popup.focus();if(showOnce)sessionStorage.setItem(key,'1');}function close(){popup.setAttribute('hidden','');shown=false;}popup.querySelector('.ease-popup-close').addEventListener('click',close);popup.querySelector('.ease-popup-backdrop').addEventListener('click',close);var cta=popup.querySelector('.ease-popup-cta[href]');if(cta){cta.addEventListener('click',close);}if(trigger==='time_delay'){setTimeout(show,${String(delaySec * 1000)});}else if(trigger==='scroll_depth'){window.addEventListener('scroll',function(){var pct=(window.scrollY/(document.documentElement.scrollHeight-window.innerHeight))*100;if(pct>=${String(scrollPct)})show();});}else if(trigger==='exit_intent'){document.addEventListener('mouseout',function(e){if(e.clientY<0)show();});}document.addEventListener('keydown',function(e){if(e.key==='Escape'&&!popup.hasAttribute('hidden'))close();});})()</script>`;

  return `<div class="ease-popup" data-ease-popup data-trigger="${attr(trigger)}" data-scroll="${attr(String(scrollPct))}" data-delay="${attr(String(delaySec))}" data-show-once="${attr(String(showOnce))}" role="dialog" aria-modal="true" aria-label="${attr(heading)}" hidden tabindex="-1">
  <div class="ease-popup-backdrop" aria-hidden="true"></div>
  <div class="ease-popup-content">
    <button type="button" aria-label="Close" class="ease-popup-close">&times;</button>
    <div class="ease-popup-inner">
      <h2 class="ease-popup-heading">${text(heading)}</h2>${contentHtml}
      ${ctaHtml}
    </div>
  </div>
</div>
${script}`;
}

registerRenderer("popup-trigger", { render });
